export interface ApiError {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiException extends Error {
  constructor(public readonly status: number, public readonly apiError: ApiError) {
    super(apiError.message);
  }
}

const API_BASE = '/api/v1';
const ACCESS_KEY = 'abase.accessToken';
const REFRESH_KEY = 'abase.refreshToken';

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}
export function setTokens(access: string, refresh?: string): void {
  localStorage.setItem(ACCESS_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
}
export function clearTokens(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

async function tryRefresh(): Promise<boolean> {
  const rt = getRefreshToken();
  if (!rt) return false;
  const r = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: rt }),
  });
  if (!r.ok) return false;
  const data = (await r.json()) as { accessToken: string };
  setTokens(data.accessToken);
  return true;
}

export interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  formData?: FormData;
  skipAuth?: boolean;
}

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (!opts.formData && opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (!opts.skipAuth) {
    const t = getAccessToken();
    if (t) headers['Authorization'] = `Bearer ${t}`;
  }

  const fetchInit: RequestInit = {
    method: opts.method ?? 'GET',
    headers,
    body: opts.formData ? opts.formData : opts.body ? JSON.stringify(opts.body) : undefined,
  };

  let response = await fetch(`${API_BASE}${path}`, fetchInit);

  if (response.status === 401 && !opts.skipAuth) {
    const ok = await tryRefresh();
    if (ok) {
      const t = getAccessToken();
      if (t) (headers as Record<string, string>)['Authorization'] = `Bearer ${t}`;
      response = await fetch(`${API_BASE}${path}`, { ...fetchInit, headers });
    }
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const data = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const apiErr: ApiError = (data && data.error) || { code: 'E_UNKNOWN', message: response.statusText };
    throw new ApiException(response.status, apiErr);
  }
  return data as T;
}
